import type { WorkTaskRelatedEntityType } from "@/api/workTasks";

/**
 * Casing bridge between the messaging surface (lowercase snake_case
 * resource-type strings — see ResourceTypes.cs) and the tasks surface
 * (PascalCase enum members — see WorkTaskRelatedEntityType.cs). Both
 * picker layers (message AttachResourceModal + task QuickAddTaskModal)
 * import this single map so that one row resolved on one surface can be
 * filed correctly on the other.
 *
 * Coverage: every entity type that has BOTH a Messages tab and a Tasks
 * tab on its detail page. Surfaces with no counterpart on the other
 * side are intentionally omitted:
 *   - tasks-only: Report, Document, None
 *   - messages-only legacy: candidate, buyer
 * ("seller" was legacy messages-only until the Party surfaces landed; it
 * now denotes the Seller party record, paired with "borrower".)
 *
 * Reunderwriting case is the one asymmetric token: it predates the
 * snake_case convention and uses a hyphen on the messaging side
 * ("reunderwriting-case") to match what the detail page wired before
 * this PR.
 */
export const MESSAGE_TYPE_TO_TASK_TYPE: Readonly<
  Record<string, WorkTaskRelatedEntityType>
> = {
  borrower: "Borrower",
  seller: "Seller",
  broker: "Broker",
  brokerage: "Brokerage",
  deal: "Deal",
  lead: "Lead",
  person: "Person",
  entity: "Entity",
  business: "Business",
  vendor: "Vendor",
  loan: "Loan",
  spv: "Spv",
  spv_pool: "SpvPool",
  investor: "Investor",
  indenture_trustee: "IndentureTrustee",
  trust_company: "TrustCompany",
  placement_agent: "PlacementAgent",
  backup_servicer: "BackupServicer",
  tps: "Tps",
  local_enforcer: "LocalEnforcer",
  portal_user: "PortalUser",
  "reunderwriting-case": "ReunderwritingCase",
  // Buyer CRM (PR 2a). `buyer_lead` -> `BuyerLead`: this IS a mechanical
  // snake->Pascal transform, but the map stays explicit because
  // "reunderwriting-case" proves the relationship is not mechanical in general.
  buyer_lead: "BuyerLead",
  // Buyer programme (PR 2). Both surfaces are live on each record's detail
  // page, so a listing or a submission picked on one can be filed on the other.
  listing: "Listing",
  buyer_submission: "BuyerSubmission",
};

export const TASK_TYPE_TO_MESSAGE_TYPE: Readonly<
  Partial<Record<WorkTaskRelatedEntityType, string>>
> = Object.freeze(
  Object.fromEntries(
    Object.entries(MESSAGE_TYPE_TO_TASK_TYPE).map(([msg, task]) => [task, msg]),
  ),
);

export function taskTypeForMessageType(
  messageType: string,
): WorkTaskRelatedEntityType | null {
  return MESSAGE_TYPE_TO_TASK_TYPE[messageType] ?? null;
}

export function messageTypeForTaskType(
  taskType: WorkTaskRelatedEntityType,
): string | null {
  return TASK_TYPE_TO_MESSAGE_TYPE[taskType] ?? null;
}
